/* eslint-disable no-confusing-arrow */
import BigNumber from 'bignumber.js'

export const DIGIT = '9'
export const ALPHA = 'A'
export const ALPHANUM = 'S'
export const HOURS = 'H'
export const MINUTES = 'm'
export const SECONDS = 's'

const currencyFormat = (
    value: string | number | null | undefined,
    prefix: string,
    precision: number,
    decimalSeparator: string,
    groupSeparator: string
) => {
    if (value === null || value === undefined) {
        value = '0'
    }

    value = `${value}`.replace(/[^0-9]+/g, '')

    return currencyMasker(value, {
        prefix: prefix,
        decimalSeparator,
        groupSeparator,
        precision,
    })
}

/**
 * function addPlaceholder
 * @param {string[]} output
 * @param {number} index
 * @param {string} placeholder
 * @returns {string[]}
 */
function addPlaceholder(
    output: string[],
    index: number,
    placeholder: string
): string[] {
    for (let newIndex = index; newIndex < output.length; newIndex++) {
        if (
            output[newIndex] === DIGIT ||
            output[newIndex] === ALPHA ||
            output[newIndex] === ALPHANUM
        ) {
            // eslint-disable-next-line no-param-reassign
            output[newIndex] = placeholder
        }
    }
    return output
}

type OptionPattern = {
    pattern: string
    placeholder: string
}

/**
 * function toPattern
 * @param {number | string} value
 * @param {string | OptionPattern} optionPattern
 * @returns {string}
 */
function toPattern(
    value: number | string,
    optionPattern: string | OptionPattern
): string {
    const pattern =
        typeof optionPattern === 'object'
            ? optionPattern.pattern
            : optionPattern
    const patternChars = pattern.replace(/\W/g, '')
    const output = pattern.split('')
    const values = value.toString().replace(/\W/g, '')
    const charsValues = values.replace(/\W/g, '')
    const placeholder =
        typeof optionPattern === 'object'
            ? optionPattern.placeholder
            : undefined
    let charCounter = 0
    let index

    const outputLength = output.length
    for (index = 0; index < outputLength; index++) {
        // Reached the end of input
        if (charCounter >= values.length) {
            if (patternChars.length === charsValues.length) {
                return output.join('')
            }
            if (
                placeholder !== undefined &&
                patternChars.length > charsValues.length
            ) {
                return addPlaceholder(output, index, placeholder).join('')
            }
            break
        } else if (
            (output[index] === DIGIT && values[charCounter].match(/[0-9]/)) ||
            (output[index] === ALPHA &&
                values[charCounter].match(/[a-zA-Z]/)) ||
            (output[index] === ALPHANUM &&
                values[charCounter].match(/[0-9a-zA-Z]/)) ||
            (output[index] === HOURS && values[charCounter].match(/[0-23]/)) ||
            (output[index] === MINUTES &&
                values[charCounter].match(/[0-59]/)) ||
            (output[index] === SECONDS && values[charCounter].match(/[0-59]/))
        ) {
            output[index] = values[charCounter++]
        } else if (
            output[index] === DIGIT ||
            output[index] === ALPHA ||
            output[index] === ALPHANUM ||
            output[index] === HOURS ||
            output[index] === MINUTES ||
            output[index] === SECONDS
        ) {
            if (placeholder !== undefined) {
                return addPlaceholder(output, index, placeholder).join('')
            }
            return output.slice(0, index).join('')

            // exact match for a non-magic character
        } else if (output[index] === values[charCounter]) {
            charCounter++
        }
    }
    return output.join('').substr(0, index)
}

/**
 * function unMask(
 * @param {string} value
 * @param {'custom' | 'currency'} type
 * @returns {string}
 */
function unMask(value: string, type: 'custom' | 'currency' = 'custom') {
    if (type === 'currency') {
        if (!value) return '0'

        const unMaskedValue = value.replace(/\D/g, '')
        const number = parseInt(unMaskedValue.trimStart())

        return number.toString()
    }

    return value.replace(/\W/g, '')
}

/**
 * function masker(
 * @param {string} value
 * @param {string} patterns
 * @param {any} options
 * @returns {string}
 */
function masker(value: string, pattern: string, options: any) {
    const { autoCapitalize } = options

    let sentence = toPattern(value, { pattern, ...options })

    switch (autoCapitalize) {
        case 'characters':
            sentence = sentence.toUpperCase()
            break
        case 'words':
            sentence = sentence.replace(/(?:^|\s)\S/g, text =>
                text.toUpperCase()
            )
            break
        case 'sentences': {
            const lower = sentence.toLowerCase()
            sentence = lower.charAt(0).toUpperCase() + lower.substring(1)
            break
        }
    }

    return sentence
}

/**
 * function masker(
 * @param {string} value
 * @param {any} options
 * @returns {string}
 */
function currencyMasker(value = '0', options: any) {
    const {
        prefix,
        decimalSeparator,
        groupSeparator,
        precision,
        groupSize,
        secondaryGroupSize,
        fractionGroupSeparator,
        fractionGroupSize,
        suffix,
    } = options

    const precisionDivider = parseInt(1 + '0'.repeat(precision || 0))
    // Campo APAGADO chega como string vazia, e o default `value = '0'` só cobre
    // `undefined`: `parseInt('')` é `NaN`, e o campo passava a exibir "R$ NaN" —
    // sem valor nenhum de onde voltar, porque o próprio texto vira o estado.
    const parsed = parseInt(value)
    const number = (Number.isNaN(parsed) ? 0 : parsed) / precisionDivider

    const formatter = {
        prefix,
        decimalSeparator,
        groupSeparator,
        groupSize: groupSize || 3,
        secondaryGroupSize,
        fractionGroupSeparator,
        fractionGroupSize,
        suffix,
    }

    const bigNumber = new BigNumber(number)

    BigNumber.config({ FORMAT: formatter })

    return bigNumber.toFormat(precision)
}

function dateMasker(value = '', options: any) {
    const { dateFormat = 'yyyy/mm/dd' } = options

    const regex = /[a-zA-Z]/gi
    const pattern = dateFormat.replaceAll(regex, '9')
    return masker(value, pattern, {})
}

function timeMasker(value = '', options: any) {
    const { timeFormat = 'HH:mm:ss' } = options

    const pattern = timeFormat
    return masker(value, pattern, {})
}

/**
 * function multimasker(
 * @param {string} value
 * @param {string[]} patterns
 * @param {any} options
 * @returns {string}
 */
function multimasker(value: string, patterns: string[], options: any) {
    return masker(
        value,
        patterns.reduce(
            (memo: string, pattern: string) =>
                value.length <= unMask(memo).length ? memo : pattern,
            patterns[0]
        ),
        options
    )
}

function mask(
    value: string | number,
    pattern: string | string[] = '',
    type: any = 'custom',
    options?: any,
    autoCapitalize?: any
): string {
    if (type === 'currency') {
        return currencyMasker(String(value), options)
    }

    if (type === 'date') {
        return dateMasker(String(value), options)
    }

    if (type === 'time') {
        return timeMasker(String(value), options)
    }

    if (typeof pattern === 'string') {
        return masker(String(value), pattern || '', {
            autoCapitalize: autoCapitalize,
        })
    }

    return multimasker(String(value), pattern, {})
}

/**
 * Formata um número de telefone brasileiro
 * Aceita formatos: 5511999999999, 11999999999, 1199999999
 * Retorna: (11) 99999-9999
 */
/**
 * Máscara de telefone.
 *
 * O formato nacional `(DD) 9XXXX-XXXX` só vale para o Brasil. Qualquer número
 * com DDI explícito de outro país é devolvido em E.164 (`+<dígitos>`), sem
 * máscara: cada país agrupa os dígitos do seu jeito, e aplicar o formato
 * brasileiro não deixa o número feio — deixa ERRADO. `+1 555-456-8597` virava
 * `(15) 55456-8597`, um celular de São Paulo.
 *
 * Sem máscara internacional de verdade (exigiria `libphonenumber-js`), o E.164
 * é a escolha certa: é inequívoco e é exatamente o que a Meta espera.
 */
function formatPhoneNumber(phone: string | number | null | undefined): string {
    if (!phone) return ''

    const raw = String(phone).trim()

    // Remove todos os caracteres não numéricos
    const digits = raw.replace(/\D/g, '')

    if (digits.length === 0) return ''

    // DDI explícito de fora do Brasil: preserva como está.
    if (raw.startsWith('+') && !digits.startsWith('55')) {
        return `+${digits}`
    }

    // Se começar com 55 (código do país), remove
    let cleanDigits = digits
    if (digits.startsWith('55') && digits.length >= 12) {
        cleanDigits = digits.substring(2)
    }

    // Se tiver 11 dígitos (DDD + 9 dígitos), formata como celular: (DD) 99999-9999
    if (cleanDigits.length === 11) {
        return `(${cleanDigits.substring(0, 2)}) ${cleanDigits.substring(
            2,
            7
        )}-${cleanDigits.substring(7)}`
    }

    // 10 dígitos = DDD + 8. Vale para fixo e para celular ANTIGO, sem o nono
    // dígito (ainda comum na base): nos dois casos a máscara é 4-4.
    //
    // Aplicar aqui a máscara de 11 dígitos porque o terceiro é 9 — como se
    // fazia — produzia "(73) 98254-574", com o último bloco faltando um dígito.
    if (cleanDigits.length === 10) {
        return `(${cleanDigits.substring(0, 2)}) ${cleanDigits.substring(
            2,
            6
        )}-${cleanDigits.substring(6)}`
    }

    // Se tiver 9 dígitos (assume formato 1199999999 - DDD + 8 dígitos), formata como fixo
    if (cleanDigits.length === 9) {
        return `(${cleanDigits.substring(0, 2)}) ${cleanDigits.substring(
            2,
            6
        )}-${cleanDigits.substring(6)}`
    }

    // Se tiver menos de 9 dígitos, retorna como está (formato incompleto)
    if (cleanDigits.length < 9) {
        return cleanDigits
    }

    // Para números maiores, pega os últimos 11 dígitos
    const last11 = cleanDigits.slice(-11)
    if (last11.length === 11) {
        return `(${last11.substring(0, 2)}) ${last11.substring(
            2,
            7
        )}-${last11.substring(7)}`
    }

    return cleanDigits
}

export function getClientLabel(segment?: string, secondarySegment?: string): string {
    return segment === 'HEALTH' && secondarySegment !== 'INTERMEDIATION' ? 'paciente' : 'cliente'
}

export function getClientLabelCapitalized(segment?: string, secondarySegment?: string): string {
    return segment === 'HEALTH' && secondarySegment !== 'INTERMEDIATION' ? 'Paciente' : 'Cliente'
}

/**
 * Vocabulário do tipo de atendimento por segmento.
 *
 * Fora da saúde, "procedimento" vira "serviço" e "consulta" vira "avaliação".
 * "Exame" é mantido: uma avaliação física numa academia ou uma revisão completa
 * numa oficina continuam sendo exames.
 *
 * Não confundir com `layouts/admin/segment-labels.ts`, que traduz o MENU
 * (label e ícone de módulo/recurso). Este helper é para texto DENTRO das telas:
 * títulos, filtros, colunas, botões e mensagens.
 */
export interface ServiceLabels {
    isHealth: boolean
    order: string
    Order: string
    orders: string
    Orders: string
    consultation: string
    Consultation: string
    consultations: string
    Consultations: string
    exam: string
    Exam: string
    exams: string
    Exams: string
    teleconsultation: string
    Teleconsultation: string
    teleconsultations: string
    Teleconsultations: string
    /**
     * Placeholder pronto do campo de nome — texto completo, não só o exemplo.
     *
     * Na saúde é um exemplo concreto ("Ex.: Drenagem linfática"), porque o ramo é
     * um só e conhecido. **Fora dela, é a descrição do campo, sem exemplo nenhum.**
     * A plataforma atende beleza, fitness, pet shop, oficina e o que vier; um
     * exemplo de um ramo só ("Corte de cabelo") estaria errado para todos os
     * outros, e manter uma tabela ramo a ramo é dívida que cresce a cada segmento
     * novo — o fallback sempre erraria alguém.
     *
     * Ficam aqui, e não nos formulários, porque os três aparecem em telas
     * diferentes (item, variação e pacote).
     */
    itemNamePlaceholder: string
    variationNamePlaceholder: string
    packageNamePlaceholder: string
    /**
     * Complemento opcional da frase "Cada variação___ é um item filho…". Vazio
     * fora da saúde, pelo mesmo motivo.
     */
    variationHint: string
}

const HEALTH_SERVICE_LABELS: ServiceLabels = {
    isHealth: true,
    order: 'procedimento',
    Order: 'Procedimento',
    orders: 'procedimentos',
    Orders: 'Procedimentos',
    consultation: 'consulta',
    Consultation: 'Consulta',
    consultations: 'consultas',
    Consultations: 'Consultas',
    exam: 'exame',
    Exam: 'Exame',
    exams: 'exames',
    Exams: 'Exames',
    teleconsultation: 'teleconsulta',
    Teleconsultation: 'Teleconsulta',
    teleconsultations: 'teleconsultas',
    Teleconsultations: 'Teleconsultas',
    itemNamePlaceholder: 'Ex.: Drenagem linfática',
    variationNamePlaceholder: 'Ex.: Com contraste',
    packageNamePlaceholder: 'Ex.: Pacote saúde da mulher',
    variationHint: ' (ex.: com contraste)',
}

const GENERIC_SERVICE_LABELS: ServiceLabels = {
    isHealth: false,
    // "Pedido", e não "Serviço": o registro passou a comportar venda de mercadoria
    // (ver docs/catalog-system.md §12), então o termo precisa cobrir serviço E
    // produto. É também o rótulo que o menu lateral já usa (`segment-labels.ts`).
    order: 'pedido',
    Order: 'Pedido',
    orders: 'pedidos',
    Orders: 'Pedidos',
    consultation: 'avaliação',
    Consultation: 'Avaliação',
    consultations: 'avaliações',
    Consultations: 'Avaliações',
    exam: 'exame',
    Exam: 'Exame',
    exams: 'exames',
    Exams: 'Exames',
    teleconsultation: 'atendimento online',
    Teleconsultation: 'Atendimento online',
    teleconsultations: 'atendimentos online',
    Teleconsultations: 'Atendimentos online',
    itemNamePlaceholder: 'Nome do item',
    variationNamePlaceholder: 'Rótulo da variação',
    packageNamePlaceholder: 'Nome do pacote',
    variationHint: '',
}

export function getServiceLabels(segment?: string): ServiceLabels {
    return segment === 'HEALTH' ? HEALTH_SERVICE_LABELS : GENERIC_SERVICE_LABELS
}

export { currencyMasker, formatPhoneNumber, mask, unMask }

